import { useState } from "react";
import { AlertTriangle, Info, Key, Loader2 } from "lucide-react";
import { createApiKey } from "../../../services/apiKeyService";
import {
  DEFAULT_EXPIRY,
  ErrorBox,
  ExpiryField,
  NEVER_EXPIRES,
  NewKeyResult,
  expiryToIso,
  serverReportedEffects
} from "./apiKeyParts";

const SLOTS = [
  { env: "production", label: "Production API Key" },
  { env: "sandbox", label: "Sandbox API Key" }
];

/**
 * Legacy mode: the backend has no list endpoint (GET /api-keys → 404/405).
 * Such a backend revokes the organization's active key with the same name on
 * every mint, so the revoke warning is shown for every expiry option.
 */
function LegacySlot({ env, label }) {
  const [open, setOpen] = useState(false);
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  const [neverAck, setNeverAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const needsAck = expiry === NEVER_EXPIRES && !neverAck;

  const generate = async () => {
    if (busy || needsAck) return;
    setBusy(true);
    setError("");
    const res = await createApiKey({
      name: env,
      expires_at: expiryToIso(expiry)
    });
    setBusy(false);
    if (!res.success) {
      setError(res.message);
      return;
    }
    if (!res.data?.raw_key) {
      setError("The server did not return the new key.");
      return;
    }
    setResult(res.data);
    setOpen(false);
    setExpiry(DEFAULT_EXPIRY);
    setNeverAck(false);
  };

  return (
    <div data-testid={`legacy-slot-${env}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="block text-sm font-medium text-gray-700">{label}</span>
        <button
          type="button"
          onClick={() => {
            setOpen((prev) => !prev);
            setResult(null);
            setError("");
          }}
          disabled={busy}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Key className="w-4 h-4" />
              Generate new {env} key
            </>
          )}
        </button>
      </div>

      {open && !result && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 space-y-3">
          <ExpiryField
            id={`legacy-expiry-${env}`}
            value={expiry}
            onChange={(value) => {
              setExpiry(value);
              setNeverAck(false);
            }}
            ack={neverAck}
            onAckChange={setNeverAck}
            disabled={busy}
          />
          <p className="flex items-start gap-2 text-xs text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              Generating a new key revokes the current one immediately.
            </span>
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={generate}
              disabled={busy || needsAck}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="mb-3">
          <NewKeyResult
            result={result}
            footnote={
              // Legacy backends revoke the previous key but don't say so. A
              // server that does report effects is taken at its word instead.
              serverReportedEffects(result)
                ? undefined
                : `Any previous ${env} key has been revoked.`
            }
            onDone={() => setResult(null)}
          />
        </div>
      )}

      <ErrorBox message={error} />
    </div>
  );
}

export default function LegacyApiKeySlots() {
  return (
    <div className="space-y-6">
      <p className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <Info className="w-4 h-4 shrink-0 text-gray-500" />
        <span>
          This server can't list existing keys yet. Generating a key for an
          environment replaces that environment's current key.
        </span>
      </p>
      {SLOTS.map((slot) => (
        <LegacySlot key={slot.env} env={slot.env} label={slot.label} />
      ))}
    </div>
  );
}
